import { useCallback, useEffect, useState } from 'react';

interface UseCountdownReturn {
  remaining: number;
  label: string;
  done: boolean;
  reset: () => void;
}

/**
 * Wall-clock countdown: remaining = deadline − now, not a per-tick decrement (E4). Mobile
 * browsers throttle/suspend a background tab's `setInterval`, so a decrement-per-tick countdown
 * shows a stale value after the user switches to their banking app and comes back. Deadline math
 * self-corrects on the next tick regardless of how long the interval was suspended; the
 * `visibilitychange` listener also forces an immediate recompute so the label updates the
 * instant the tab becomes visible again, instead of waiting for the next (possibly delayed) tick.
 */
export function useCountdown(
  seconds: number,
  running: boolean
): UseCountdownReturn {
  const [deadline, setDeadline] = useState(() => Date.now() + seconds * 1000);
  const [remaining, setRemaining] = useState(() =>
    Math.max(0, Math.ceil((deadline - Date.now()) / 1000))
  );

  const recompute = useCallback(() => {
    setRemaining(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));
  }, [deadline]);

  useEffect(() => {
    recompute();
    if (!running) return;
    const t = setInterval(recompute, 1000);
    return () => clearInterval(t);
  }, [running, recompute]);

  useEffect(() => {
    if (!running) return;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') recompute();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [running, recompute]);

  const reset = useCallback(() => {
    setDeadline(Date.now() + seconds * 1000);
  }, [seconds]);

  const label = `${String(Math.floor(remaining / 60)).padStart(2, '0')}:${String(
    remaining % 60
  ).padStart(2, '0')}`;

  return { remaining, label, done: remaining === 0, reset };
}
